/**
 * Comprehensive Test Data Loader Module
 * Centralizes all test data loading for development mode
 * Removes test concepts from the main store
 */

// Import test articles
import getTestArticleSet from './testArticleLoader.js';

// Import other test data
import pdfTestIssue from './Pdf/pdfTestIssue.js';
import audiobookTestIssue from './Audiobook/audiobookTestIssue.js';
import epubTestIssue from './Epub/epubTestIssue.js';
import testTableOfContents from './Pdf/testTableOfContents.js';
import testFilesUrls from './Pdf/testFilesUrls.js';
import testFilesUrlsStress532 from './Pdf/testFilesUrlsWith532Pages.js';
import audiobookTestPlaylist from './Audiobook/audiobookTestPlaylist.js';
import pdfTestHighlights from './Pdf/pdfTestHighlights.js';
import epubTestHighlights from './Epub/epubTestHighlights.js';

// Import EPUB test configuration
import { getTestEpubFile } from './Epub/testEpubFiles.js';

/**
 * Get complete test data for development mode
 * @returns {Object} Complete test data object ready to be committed to store
 */
export function getTestData() {
    // Get the appropriate test article set based on URL params
    const articles = getTestArticleSet();

    // Determine file type from URL params or default to PDF
    const urlParams = new URLSearchParams(window.location.search);
    const fileType = urlParams.get('file_type') || 'pdf';

    // Select PDF page set: default (local copesa) or stress-532 (532-page stress test)
    const pdfSet = urlParams.get('pdf_set');
    const pdfFilesUrls = pdfSet === 'stress-532' ? testFilesUrlsStress532 : testFilesUrls;

    // Select appropriate test data based on file type (explicit for clarity)
    const issue = fileType === 'audio' ? audiobookTestIssue : fileType === 'epub' ? epubTestIssue : pdfTestIssue;
    const highlights = fileType === 'epub' ? epubTestHighlights : pdfTestHighlights;

    // Get EPUB configuration if file type is EPUB
    const epubConfig = fileType === 'epub' ? getTestEpubFile() : null;

    return {
        // Issue data
        issue,

        // Publication data
        publication: {
            title: 'Volpe - DEMO',
            table_of_contents: testTableOfContents,
            files_urls: pdfFilesUrls,
            files_info: pdfFilesUrls,
            cover: issue.cover,
            url: null,
        },

        // Smart Zoom articles
        articles,
        currentPageArticles: articles,

        // Audiobook data
        audiobook: {
            playlist: audiobookTestPlaylist,
            chapters: null,
            duration: null,
            currentTrackDurationInSeconds: null,
            speed: 1,
            track: {
                durationInSeconds: null,
            },
            trackPlayer: {
                isLoading: false,
                isPlaying: false,
            },
        },

        // Highlights/selections
        highlights,

        // Promote app data
        promoteApp: {
            enabled: true,
            android_app_link: 'https://play.google.com/store/apps/details?id=la.publica.reader',
            apple_app_link: 'https://apps.apple.com/app/apple-store/id1601992631?pt=119056122',
            app_icon: 'https://i.ibb.co/rcGxxLH/logo-app.jpg',
            app_name: 'publica.la Reader',
        },

        // Test mode configuration
        testMode: {
            anonymousSession: false,
            enforceConcurrencyLimit: false,
            debugEnforceConcurrencyLimit: false,
            featureFlags: 'StandAloneAllFeatures',
        },

        // EPUB configuration (only when fileType is 'epub')
        epub: epubConfig
            ? {
                  filePath: epubConfig.path,
                  name: epubConfig.name,
                  description: epubConfig.description,
                  cover: epubConfig.cover,
              }
            : null,
    };
}

/**
 * Apply test data to the store
 * Helper function to commit all test data to appropriate store locations
 */
export function applyTestDataToStore(store, testData) {
    // Set issue data
    store.commit('setFakeIssue', { issue: testData.issue });

    // Set publication data
    store.commit('title', { title: testData.publication.title });
    store.commit('filesUrls', { files_urls: testData.publication.files_urls });
    store.commit('filesInfo', { files_info: testData.publication.files_info });
    store.commit('tableOfContents', { table_of_contents: testData.publication.table_of_contents });
    if (testData.publication.cover) {
        store.commit('publicationCover', { cover: testData.publication.cover });
    }

    // Set Smart Zoom articles
    store.commit('setIssueArticles', { articles: testData.articles });
    store.commit('setCurrentPageArticles', { currentPageArticles: testData.currentPageArticles });

    // Set audiobook data
    store.commit('loadingAudiobookPlaylist', { playlist: testData.audiobook.playlist });

    // Set highlights (commented out in original code)
    // store.commit('setHighlights', { highlights: testData.highlights });

    // Set promote app data
    store.commit('setPromoteAppData', testData.promoteApp);

    // Set test mode configurations
    store.commit('anonymousSession', { anonymous_session: testData.testMode.anonymousSession });
    store.commit('setEnforceConcurrencyLimit', testData.testMode.enforceConcurrencyLimit);
    store.commit('setDebugEnforceConcurrencyLimit', testData.testMode.debugEnforceConcurrencyLimit);
    store.commit(testData.testMode.featureFlags);

    // Set EPUB configuration if provided
    if (testData.epub) {
        store.commit('setEpubFilePath', { filePath: testData.epub.filePath });
    }
}

export default getTestData;
